import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, EventEmitter } from '@angular/core';
import { BuildingService } from '../../../../resources/building/building.service';
import { ClusterService } from '../../../../resources/cluster/cluster.service';
import { AreaService } from '../../../../resources/area/area.service';
import { BasicAreaService } from '../../../../resources/basic-area/basic-area.service';
import { Building } from '../../../../resources/building/building.interface';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { RouterService } from '../../../../utilities/router/router.service';
import { AuthService } from '../../../../auth/auth.service';
import { filter, map, takeUntil, skip, tap } from 'rxjs/operators';
import { Area } from '../../../../resources/area/area.interface';
import { BasicArea } from '../../../../resources/basic-area/basic-area.interface';
import { Subject } from 'rxjs';
import { ProjectService } from '../../../../resources/project/project.service';
import { ModalService } from '../../../../services/modal/modal.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'sg2-areas-view',
  templateUrl: './areas-view.component.html',
  styleUrls: ['./areas-view.component.less']
})
export class AreasViewComponent implements  OnDestroy, OnInit {

  /** Current active building. */
  public building: Building;

  /** Current visible clusters. */
  public clusters: Cluster[];

  /** Total surface for cluster. */
  public clusterSurfaceTotal: {
    [key: string]: number;
  } = {};

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** List of id's of every visible cluster, for the drag-drop zones. */
  public dropListIds: Cluster['_id'][];

  /** Whether a cluster is currently being dragged. */
  public onDragCluster = false;

  /** Binding for legacy Material dialog. */
  private $mdDialog: any;

  /** Binding for AngularJS $scope. */
  private $scope: any;

  /** Route params. */
  private params: {
    buildingId: Building['_id'],
    projectId: Project['_id'],
  };

  /** Table columns. */
  public tableColumns = [
    'code',
    'name',
    'template',
    'functionality',
    'quantity',
    'occupation',
    'height',
    'surface',
    'surfaceTotal',
    'options'
  ];

  constructor(
    @Inject('$mdDialog') $mdDialog: any,
    @Inject('$scope') $scope: any,
    private areaService: AreaService,
    public authService: AuthService,
    public basicAreaService: BasicAreaService,
    private buildingService: BuildingService,
    private clusterService: ClusterService,
    private modalService: ModalService,
    private projectService: ProjectService,
    private routerService: RouterService
  ) {
    this.$mdDialog = $mdDialog;
    this.$scope = $scope;
  }

  ngOnInit() {
    this.params = this.routerService.getParams();
    this.building = this.buildingService.getBuilding(this.params.buildingId);
    // this.clusters = this.clusterService.getClusterByBuildingId(this.building._id)
    //   .filter(cluster => cluster.active)
    //   .map(cluster => cluster = this._setAreaCustomValues(cluster))
    //   .map(cluster => cluster = { ...cluster, areas: cluster.areas.filter(a => a.active) });

    this._trackBuilding();
    this._trackClusters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Add new area.
   *
   * @param cluster The parent cluster.
   */
  public addArea(cluster: Cluster): void {
    this.areaService.add(this.building._id, cluster._id, {}, true);
  }

  /**
   * Add new cluster.
   */
  public addCluster(): void {
    this.clusterService.add(this.building, null, true);
  }

  /**
   * Enable cluster drag mode.
   *
   * When this mode is active, the template changes to a more compact view,
   * where tables and FNO/BVO are hidden, to make it easier to sort the clusters.
   *
   * When active, a class is also applied to the view-container (`on-drag-cluster`),
   * to allow CSS to make changes as well.
   */
  public enableDragCluster(): void {
    this.onDragCluster = true;
  }

  /**
   * Disable cluster drag mode.
   */
  public disableDragCluster(): void {
    this.onDragCluster = false;
  }

  /**
   * Go to area detail page.
   *
   * To prevent a redirect on interactive elements (forms, buttons), add
   * the `.row-clickable-exclude` class on the table cell.
   *
   * @param id Area id of the detail page.
   */
  public goToArea(area: Area, $event: MouseEvent): void {
    const el = $event.target as HTMLElement;
    if (el.closest('.mat-cell').className.indexOf('row-clickable-exclude') > -1) {
      $event.preventDefault();
    } else {
      this.routerService.goToArea(area._id);
    }
  }

  /**
   * On dropping area.
   *
   * Extract previous and new cluster id and area index from the event data.
   *
   * @param event The dropping event.
   */
  public onDropArea(event: CdkDragDrop<Area[]>): void {
    this.areaService.move(event.item.data, {
      id: event.previousContainer.id.replace('table-', ''),
      index: event.previousIndex
    }, {
      id: event.container.id.replace('table-', ''),
      index: event.currentIndex
    });
  }

  /**
   * On dropping cluster.
   *
   * When the cluster is dropped, the order should be updated.
   *
   * @param event The dropping event.
   */
  public onDropCluster(event: CdkDragDrop<Cluster[]>): void {
    this.clusterService.move(event.item.data, event.previousIndex, event.currentIndex);
  }

  /**
   * Calculate total area surface for a cluster.
   *
   * @param clusters Clusters to calculate the total area surface for.
   */
  private _calculateClusterSurfaceTotal(clusters: Cluster[]): void {
    for (const cluster of clusters) {
      this.clusterSurfaceTotal[cluster._id] = cluster.areas.reduce(
        (sum, item) => sum + (item.surface * item.quantity),
      0);
    }
  }

  /**
   * Generate list of table / dropzone id's.
   *
   * The list if updated on every change (to account for creating/deleting clusters).
   */
  private _generateDropListIds(): void {
    this.dropListIds = JSON.parse(JSON.stringify(this.clusters))
      .map((c: Cluster) => 'table-' + c._id);
  }

  /**
   * Set custom values for areas.
   *
   * Create a local lookup table for variables used in the table,
   * to prevent calling `get()` on a service *each* digest cycle.
   *
   * @param cluster Parent cluster, containing the areas.
   * @returns Cluster with areas including the `customValues` property.
   */
  private _setAreaCustomValues(cluster: Cluster): Cluster {
    for (const area of cluster.areas) {
      const areaTemplate = this.basicAreaService.getBasicArea(area.basicAreaId);
      area.customValues = {
        areaTemplateId: areaTemplate._id,
        areaTemplateName: areaTemplate.name
      };
    }
    return cluster;
  }

  /**
   * Keep track of parent building.
   */
  private _trackBuilding(): void {
    this.buildingService.building$.pipe(
      skip(1),
      takeUntil(this.destroy$),
      map(buildings => buildings.find(b => b._id === this.params.buildingId))
    ).subscribe(building => {
      this.building = building;
    });
  }

  /**
   * Keep track of clusters.
   *
   * Pipe:
   * - Filter clusters by current building
   * - Filter inactive clusters
   * - Remove all inactive areas
   * - Set area custom values
   *
   * @todo Combine 3 and 4.
   */
  private _trackClusters(): void {
    this.clusterService.cluster$.pipe(
      takeUntil(this.destroy$),
      map(clusters => clusters.filter(cl => cl.buildingId === this.building._id)),
      map(clusters => clusters.filter(cl => cl.active)),
      map(clusters => clusters.map(cl => cl = { ...cl, areas: cl.areas.filter(a => a.active) })),
      map(clusters => clusters.map(cl => cl = this._setAreaCustomValues(cl))),
      tap(clusters => this._calculateClusterSurfaceTotal(clusters))
    ).subscribe(clusters => {
      this.clusters = clusters;
      this._generateDropListIds();
    });
  }
}
